// A Service vessel's entry form, opened from its Entry form button (the Board card's header, the Sheet's
// vessel row) or from a saved form listed in its open card: the customer's own Vessel Entry & Work Control
// Form (ΕΝΤΥΠΟ ΕΙΣΟΔΟΥ ΣΚΑΦΟΥΣ & ΕΛΕΓΧΟΥ ΕΡΓΑΣΙΩΝ), its five sections in the form's order with the form's
// bilingual labels (features/vesselEntryCopy.ts); section 2 is the revised arrival inspection (A. visual
// condition and B. basic functional check, each row OK / DEF / N/T / N/A with a comment, C. who inspected
// it, then Photos). It is the boat yard record the Documents page keeps
// (boatDocs), kept with the vessel by its row id (features/vesselEntry.ts), and prints as the paper form
// (features/vesselEntryPrint.ts):
//   - a vessel with no form opens a new one, filled in from its row on the Service sheet; a vessel with a
//     form opens that one, to change or print again, never a second. A closed form (handed over: it has a
//     Handover Date) offers New form: a boat that comes back is a new visit. A vessel keeps one open form:
//     if someone else saves the visit's form first, the worker turns the second one away and this offers to
//     open theirs; an edit can't leave a second one open either, and one saved by someone else since it was
//     opened is turned away rather than written over (Load it).
//   - Save, Save and print, Print (what is on screen, saved or not), Print blank form and Cancel. What came
//     of them (not saved, turned away, the print window blocked) is said in the footer beside them, with
//     the one action that resolves it: the form is long, and a note at its top would be out of sight.
//     Closing it with changes not saved (Cancel, the x, Escape, a click beside it, the back gesture) asks
//     first.
//   - a record saved before this form (the plain entry/exit one, with none of the rest of this form) stays
//     that record, printing as before, unless some of the rest of this form is filled in.
//   - without service:write: the saved form, read only, and Print.
// One column on phones (a bottom sheet with Save pinned at the bottom), two where the paper has two.
// A Modal portalled to <body>, like the work photos.

import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  BoatDocInputSchema, MODEL_ORDER, VESSEL_ENTRY_LIMITS,
  type BoatDocRecord, type CheckResult, type InspectionCheck, type PhotosAnswer, type VesselEntryControl,
} from '@technohull/shared';
import { useHins, useSaveBoatDoc } from '../features/hooks.js';
import { printVesselEntry, type VesselEntryPrintFormat } from '../features/vesselEntryPrint.js';
import {
  canStartNewVisit, controlOf, fieldsOf, formToOpen, handoverBeforeArrival, isClosed, lastHandoverOf, openForm, vesselEntryPrefill,
  type BoatDocFields, type EntryVessel, type VesselEntryDraft,
} from '../features/vesselEntry.js';
import {
  VEF_DISCLAIMER, VEF_ENGINES_NOTE, VEF_FUNCTIONAL, VEF_LABELS as L, VEF_PARTS as P, VEF_PHOTOS, VEF_RESULTS, VEF_SECTIONS as S, VEF_VISUAL,
  type Bi,
} from '../features/vesselEntryCopy.js';
import { useAuth } from '../auth/AuthContext.js';
import { useDateFormatter } from '../lib/format.js';
import { localTodayIso } from '../lib/dates.js';
import { useIsCoarse } from '../lib/media.js';
import { Modal, useConfirm } from './ui.js';
import { IconPrinter } from './icons.js';

/** a filled-in form: a page with a folded corner and lines on it (the icons.tsx grid and stroke). */
export const IconEntryForm = ({ size = 15 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false" style={{ flexShrink: 0 }}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <path d="M14 2v6h6" />
    <path d="M8 13h8M8 17h5" />
  </svg>
);

const GREEK = /[Ͱ-Ͽἀ-῿]/;
const lang = (s: string) => (GREEK.test(s) ? 'el' : undefined);
export const PRINT_BLOCKED = 'The print window was blocked. Allow pop-ups for this site and try again.';

/** the record's name for a vessel's saved form, in its card and on Documents. */
export const CONTROL_FORM_NAME = 'Entry & work control form';

const sameAs = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/** a bilingual label: the Greek half, then the English (under it on phones). */
function BiLabel({ b }: { b: Bi }) {
  return (
    <span className="vef-bi">
      <span lang="el" className="vef-el">{b.el}</span>
      <span className="vef-sep" aria-hidden="true"> / </span>
      <span lang="en" className="vef-en">{b.en}{b.tail ? <> / <span lang="el">{b.tail}</span></> : null}</span>
    </span>
  );
}

/** a section of the form, under its navy bar as on the paper. */
function Section({ b, children, sub }: { b: Bi; children: ReactNode; sub?: boolean }) {
  const id = useId();
  return (
    <section className={`vef-sec${sub ? ' vef-sec--sub' : ''}`} aria-labelledby={id}>
      <h4 className="vef-bar" id={id}><BiLabel b={b} /></h4>
      {children}
    </section>
  );
}

/** a row's OK / DEF / N/T / N/A (or Photos' Yes / No): one of them, pressed again to clear. */
function Choice<V extends string>({ value, options, onChange, label, disabled }: {
  value: V | '';
  options: readonly { value: V; mark: ReactNode; title: string }[];
  onChange: (v: V | '') => void;
  label: string;
  disabled: boolean;
}) {
  return (
    <div className="vef-res" role="group" aria-label={label}>
      {options.map((o) => {
        const on = value === o.value;
        return (
          <button
            key={o.value}
            type="button"
            className="vef-res-btn"
            data-v={o.value}
            aria-pressed={on}
            disabled={disabled}
            title={on ? `${o.title} (press again to clear)` : o.title}
            onClick={() => onChange(on ? '' : o.value)}
          >{o.mark}</button>
        );
      })}
    </div>
  );
}

const RESULT_OPTIONS = VEF_RESULTS.map((r) => ({ value: r.value, mark: r.mark, title: `${r.mark}: ${r.meaning.en}` }));
const PHOTO_OPTIONS = VEF_PHOTOS.map((p) => ({
  value: p.value,
  mark: <><span lang="el">{p.label.el}</span> / <span lang="en">{p.label.en}</span></>,
  title: p.label.en,
}));

/** one of the arrival check's tables (A. visual, B. functional): a row per item, its result and comment. */
function CheckTable<K extends string>({ rows, values, onChange, ro }: {
  rows: readonly { key: K; label: Bi }[];
  values: Record<K, InspectionCheck>;
  onChange: (k: K, patch: Partial<InspectionCheck>) => void;
  ro: boolean;
}) {
  return (
    <div className="vef-eq">
      <div className="vef-eq-row vef-eq-head" aria-hidden="true">
        <span><BiLabel b={L.item} /></span>
        <span><BiLabel b={L.result} /></span>
        <span><BiLabel b={L.comment} /></span>
      </div>
      {rows.map(({ key, label }) => {
        const row = values[key];
        return (
          <div key={key} className="vef-eq-row" data-result={row.result || undefined}>
            <span className="vef-eq-l"><BiLabel b={label} /></span>
            <Choice<CheckResult>
              value={row.result}
              options={RESULT_OPTIONS}
              onChange={(v) => onChange(key, { result: v })}
              label={`${label.en}: OK, DEF, N/T or N/A`}
              disabled={ro}
            />
            <span className="vef-eq-r">
              <input
                className="field"
                value={row.comment}
                maxLength={VESSEL_ENTRY_LIMITS.comment}
                onChange={(e) => onChange(key, { comment: e.target.value })}
                readOnly={ro}
                aria-label={`${label.en}: comment`}
                placeholder={ro ? undefined : `${L.comment.el} / ${L.comment.en}`}
              />
            </span>
          </div>
        );
      })}
    </div>
  );
}

export function VesselEntryForm({ vessel, forms, formId, canWrite, onClose }: {
  vessel: EntryVessel;
  /** the vessel's saved forms, newest first (formsByVessel) */
  forms: BoatDocRecord[];
  /** open this saved form rather than the newest (a form picked in the card) */
  formId?: number;
  canWrite: boolean;
  onClose: () => void;
}) {
  const { can, me } = useAuth();
  const df = useDateFormatter();
  const qc = useQueryClient();
  const save = useSaveBoatDoc();
  const modelsId = useId();
  const photosId = useId();
  const myName = me?.user.displayName || me?.user.email || '';
  // the saved form on screen, or null for a new one. It stays put while the list refreshes behind it.
  const [shown, setShown] = useState<BoatDocRecord | null>(() => formToOpen(forms, formId));
  const isNew = shown == null;
  // the HIN registry gives a new form its model when the sheet and the name don't, and the record its HIN
  // (only for those who may read Vessel Identification)
  const hins = useHins(isNew && canWrite && can('hin:read'));
  const fresh = () => vesselEntryPrefill(vessel, hins.data?.hins, { today: localTodayIso(), me: myName, lastHandover: lastHandoverOf(forms) });
  const [first] = useState<VesselEntryDraft>(() => (shown ? { fields: fieldsOf(shown), control: controlOf(shown) } : fresh()));
  const [form, setForm] = useState<BoatDocFields>(first.fields);
  const [control, setControl] = useState<VesselEntryControl>(first.control);
  // what the form opened with (the saved form, or a new one as filled in): what counts as a change
  const [base, setBase] = useState<VesselEntryDraft>(first);
  // what was typed since the form opened: the registry answering late never writes over it
  const typed = useRef(new Set<keyof BoatDocFields>());
  const [printErr, setPrintErr] = useState(false);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isNew || !hins.data) return;
    const pre = vesselEntryPrefill(vessel, hins.data.hins, { today: '', me: '' }).fields;
    const fill = (f: BoatDocFields): BoatDocFields => ({
      ...f,
      hin: typed.current.has('hin') || f.hin ? f.hin : pre.hin,
      model: typed.current.has('model') || f.model ? f.model : pre.model,
    });
    setForm(fill);
    // filled in for the person, so not a change of theirs
    setBase((b) => ({ ...b, fields: fill(b.fields) }));
  }, [hins.data, isNew]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k: keyof BoatDocFields) => (e: { target: { value: string } }) => {
    typed.current.add(k);
    setForm((f) => ({ ...f, [k]: e.target.value }));
  };
  const setCtl = <K extends keyof VesselEntryControl>(k: K, v: VesselEntryControl[K]) => setControl((c) => ({ ...c, [k]: v }));
  const text = (k: 'inspectedBy' | 'inspectedOn' | 'technician' | 'registeredBy' | 'criticalItems' | 'registeredOn' | 'worksCommenced' | 'estimatedCompletion') =>
    (e: { target: { value: string } }) => setCtl(k, e.target.value);
  const tick = (k: 'rolodexEntry' | 'jobFileOpened' | 'workRequestReceived' | 'scopeIssued') =>
    (e: { target: { checked: boolean } }) => setCtl(k, e.target.checked);
  const setCheck = <T extends 'visual' | 'functional'>(table: T) => (k: keyof VesselEntryControl[T], patch: Partial<InspectionCheck>) =>
    setControl((c) => ({ ...c, [table]: { ...c[table], [k]: { ...c[table][k], ...patch } } }));

  const controlChanged = !sameAs(control, base.control);
  const dirty = canWrite && (controlChanged || !sameAs(form, base.fields));
  // a record saved before this form (none of the rest of it) is saved without it, so it stays the plain
  // entry/exit record it was (its Entry and Exit forms still print on Documents), until some of it is filled in
  const plain = !!shown && shown.control == null && !controlChanged;
  // an edit carries the version it was loaded at, so the worker turns it away if someone saved it since
  const check = BoatDocInputSchema.safeParse({
    ...form, ...(plain ? {} : { control }), ...(shown ? { id: shown.id, updatedAt: shown.updatedAt } : {}), serviceRowId: String(vessel.rowId),
  });
  const busy = save.isPending;

  const fmt: VesselEntryPrintFormat = { date: (iso) => df.date(iso) };
  // what is on screen, for the printout (saved or not)
  const print = (): boolean => {
    const ok = printVesselEntry({ ...form, control }, fmt);
    setPrintErr(!ok);
    return ok;
  };
  const printBlank = () => setPrintErr(!printVesselEntry(null, fmt));
  const onSave = (andPrint: boolean) => {
    if (!check.success || busy) return;
    // the print window opens in the click itself (one opened once the save answers would be blocked). If it
    // can't open, nothing is saved either, so pressing it again after allowing pop-ups does both.
    if (andPrint && !print()) return;
    // the footer then speaks of this save, not of an earlier blocked print
    if (!andPrint) setPrintErr(false);
    save.mutate(check.data, {
      onSuccess: onClose,
      // turned away (the visit's form saved elsewhere meanwhile, or this one changed since it was opened):
      // bring the list up to date, so the dialog can say why and offer the saved one
      onError: () => { void qc.invalidateQueries({ queryKey: ['boat-docs'] }); },
    });
  };
  const load = (next: VesselEntryDraft) => {
    setForm(next.fields);
    setControl(next.control);
    setBase(next);
  };
  const startNew = () => {
    typed.current.clear();
    save.reset();
    setPrintErr(false);
    setShown(null);
    load(fresh());
  };
  const openSaved = (r: BoatDocRecord) => {
    typed.current.clear();
    save.reset();
    setShown(r);
    load({ fields: fieldsOf(r), control: controlOf(r) });
  };

  // Cancel, the x, Escape, a click beside the dialog and the back gesture all land here: changes not
  // saved ask first (a slip of Escape in a field, or a tap beside it, would lose the whole form)
  const coarse = useIsCoarse();
  const [confirmNode, confirm] = useConfirm();
  // the back gesture has spent the dialog's history entry by the time it asks: Keep editing re-arms it
  const [armKey, setArmKey] = useState(0);
  const bodyScroll = useRef(0);
  const requestClose = async () => {
    if (!dirty) { onClose(); return; }
    const viaBack = coarse && (window.history.state as { __overlay?: number } | null)?.__overlay == null;
    const discard = await confirm({
      title: 'Discard changes?',
      message: `The entry & work control form for ${vessel.name.trim() || 'this vessel'} has changes that are not saved.`,
      confirmLabel: 'Discard',
      cancelLabel: 'Keep editing',
      danger: true,
    });
    if (discard) onClose();
    else if (viaBack) {
      // the dialog is put back where it was scrolled to
      bodyScroll.current = root.current?.closest('.modal-bd')?.scrollTop ?? 0;
      setArmKey((k) => k + 1);
    }
  };
  useEffect(() => {
    const bd = root.current?.closest('.modal-bd');
    if (armKey && bd) bd.scrollTop = bodyScroll.current;
  }, [armKey]);
  // what the vessel's list (loaded late, or brought up to date after a save was turned away) says now:
  //  - the form on screen was saved again, or removed, since it was opened: load it rather than write over it
  const now = shown ? forms.find((f) => f.id === shown.id) : undefined;
  const outdated = !!shown && (!now || now.updatedAt !== shown.updatedAt);
  //  - the vessel's other open form: a vessel keeps one, so a new form is this visit logged twice, and an
  //    edit can't be saved open beside it
  const other = openForm(forms, shown?.id);
  const taken = canWrite && !shown && !!other;
  const clash = canWrite && !!shown && !outdated && !!other && !form.exitDate.trim();
  //  - handed over before it arrived: the sheet's Delivery date was an earlier visit's, or a date is wrong
  const backwards = handoverBeforeArrival(form);
  const blocked = taken || clash || outdated || backwards;

  // New form and Open it go away under the keyboard, and Save turns disabled while saving or once refused:
  // focus then falls to <body>, where Escape no longer reaches the dialog. Bring it back to the dialog.
  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      const box = root.current?.closest<HTMLElement>('[role="dialog"]');
      const at = document.activeElement;
      if (box && (!at || at === document.body)) box.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(raf);
  }, [shown, busy, blocked]);

  // the line under the vessel's name: where this form stands
  const state = !shown
    ? forms[0] && isClosed(forms[0]) ? 'New visit, not saved yet' : 'Not saved yet'
    : isClosed(shown)
      ? `Handed over · ${shown.entryDate ? df.range(shown.entryDate, shown.exitDate) : df.date(shown.exitDate)}`
      : `Saved · changed ${df.dateTime(shown.updatedAt, { year: 'auto' })}`;
  // a new visit once the boat has been handed over: from its newest form only (an older one is just looked at)
  const offerNew = canWrite && !!shown && isClosed(shown) && canStartNewVisit(forms) && forms[0]?.id === shown.id;
  const ro = !canWrite;
  // a handover date on a visit still open closes it once saved (a new form may take it from the sheet)
  const closing = canWrite && !!form.exitDate.trim() && !(shown && isClosed(shown));

  // the footer's one line, beside the buttons that were pressed (whoever presses Save is at the foot of a
  // long form): what came of the last of them, else what stops a save, with the one action that resolves it
  const status: { tone: 'err' | 'warn' | 'info'; text: string; act?: { label: string; run: () => void } } | null =
    printErr ? { tone: 'err', text: PRINT_BLOCKED }
    : outdated ? (now
      ? {
        tone: 'warn',
        text: `This form was saved again since you opened it (perhaps from another screen). ${canWrite ? 'Load what is saved now, then make your change.' : 'Load it to see what is saved now.'}`,
        act: { label: 'Load it', run: () => openSaved(now) },
      }
      : { tone: 'warn', text: 'This form was removed since you opened it (perhaps on the Documents page). You can still print what is on screen.' })
    : taken ? {
      tone: 'warn',
      text: 'A form for this visit is saved already (perhaps from another screen a moment ago).',
      act: { label: 'Open it', run: () => openSaved(other!) },
    }
    : clash ? {
      tone: 'warn',
      text: `${other!.entryDate ? `The vessel's form from ${df.date(other!.entryDate)} is still open` : 'Another form for this vessel is still open'}, and a vessel keeps one open form. Give this one a Handover Date, or open that one.`,
      act: { label: 'Open it', run: () => openSaved(other!) },
    }
    : save.isError ? { tone: 'err', text: `Not saved: ${save.error instanceof Error ? save.error.message : 'something went wrong'}` }
    : ro ? null
    : !check.success ? { tone: 'info', text: check.error.issues[0]?.message ?? 'Check the form' }
    : backwards ? { tone: 'warn', text: 'The Handover Date is before the Actual Arrival' }
    : null;
  const statusLine = status && (
    // a new message is announced: an error at once, a refusal politely
    <span key={status.text} className="vef-problem" data-tone={status.tone} role={status.tone === 'err' ? 'alert' : 'status'}>
      <span className="vef-problem-t">{status.text}</span>
      {status.act && <button type="button" className="btn btn-sm vef-problem-act" onClick={status.act.run}>{status.act.label}</button>}
    </span>
  );

  const field = (b: Bi, input: ReactNode, cls = '') => (
    <label className={`vef-f${cls ? ` ${cls}` : ''}`}>
      <span className="vef-l"><BiLabel b={b} /></span>
      {input}
    </label>
  );
  const dateInput = (value: string, onChange: (e: { target: { value: string } }) => void) => (
    <input type="date" className="field" value={value} onChange={onChange} readOnly={ro} />
  );
  const box = (b: Bi, on: boolean, onChange: (e: { target: { checked: boolean } }) => void) => (
    <label className="vef-box">
      <input type="checkbox" checked={on} onChange={onChange} disabled={ro} />
      <BiLabel b={b} />
    </label>
  );
  const area = (b: Bi, value: string, onChange: (e: { target: { value: string } }) => void, max: number, rows: number) =>
    field(b, <textarea className="field vef-ta" rows={rows} value={value} maxLength={max} onChange={onChange} readOnly={ro} />, 'vef-wide');

  return createPortal(
    <>
      <Modal
        key={armKey}
        title="Vessel entry & work control form"
        onClose={() => void requestClose()}
        closeLabel="Close the entry form"
        width={880}
        className="vef-modal"
        footer={ro ? (
          <>
            {statusLine}
            <button type="button" className="btn btn-ghost vef-cancel" onClick={onClose}>Close</button>
            <button type="button" className="btn btn-primary" onClick={print}><IconPrinter size={15} /> Print</button>
          </>
        ) : (
          <>
            {statusLine}
            <button type="button" className="btn btn-ghost vef-cancel" onClick={() => void requestClose()} disabled={busy}>Cancel</button>
            <button type="button" className="btn" onClick={print} title="Print what is on screen, saved or not">
              <IconPrinter size={15} /> Print
            </button>
            <button type="button" className="btn" disabled={!check.success || busy || blocked} onClick={() => onSave(true)}>Save and print</button>
            <button type="button" className="btn btn-primary" disabled={!check.success || busy || blocked} onClick={() => onSave(false)}>{busy ? 'Saving…' : 'Save'}</button>
          </>
        )}
      >
        <div className="vef" ref={root}>
          <div className="vef-head">
            <div className="vef-who">
              <span className="vef-name" lang={lang(vessel.name)}>{vessel.name.trim() || 'Unnamed vessel'}</span>
              <span className="vef-state">{state}</span>
            </div>
            <button type="button" className="btn btn-ghost btn-sm vef-blank" onClick={printBlank} title="Print the form with nothing filled in, to complete by hand">
              <IconPrinter size={14} /> Print blank form
            </button>
          </div>

          {offerNew && (
            <div className="vef-note">
              <span>The boat was handed over on {df.date(shown.exitDate)}. Back at the yard? Its new visit gets a new form.</span>
              <button type="button" className="btn btn-sm" onClick={startNew}>New form</button>
            </div>
          )}
          {canWrite && shown && shown.control == null && (
            <p className="vef-hint vef-hint--end vef-plain">
              This visit was saved with the earlier entry form, and saving it keeps it that way (its Entry and Exit forms still print from Documents) unless you fill in some of the rest of this form.
            </p>
          )}

          <Section b={S.vessel}>
            <div className="vef-grid">
              {field(L.vesselName, <input className="field" value={form.boatName} maxLength={160} onChange={set('boatName')} readOnly={ro} lang={lang(form.boatName)} />)}
              <div className="vef-f" role="group" aria-label="Vessel Type / Model / SAP No.">
                <span className="vef-l"><BiLabel b={L.typeModelSap} /></span>
                <span className="vef-pair">
                  <input className="field" list={modelsId} value={form.model} maxLength={80} onChange={set('model')} readOnly={ro} aria-label="Vessel type / model" placeholder={ro ? undefined : 'Model'} />
                  <datalist id={modelsId}>{MODEL_ORDER.map((m) => <option key={m} value={m} />)}</datalist>
                  <input className="field" value={form.sap} maxLength={40} onChange={set('sap')} readOnly={ro} aria-label="SAP No." placeholder={ro ? undefined : 'SAP No.'} inputMode="numeric" />
                </span>
              </div>
            </div>
          </Section>

          <Section b={S.arrival}>
            <div className="vef-grid">
              {field(L.arrival, dateInput(form.entryDate, set('entryDate')))}
            </div>
            <p className="vef-intro vef-disclaimer">
              <span lang="el">{VEF_DISCLAIMER.el}</span>{' '}
              <span lang="en">{VEF_DISCLAIMER.en}</span>
            </p>
            <p className="vef-legend">
              {VEF_RESULTS.filter((r) => r.inLegend).map((r) => (
                <span key={r.value}><b>{r.mark}</b> = <span lang="el">{r.meaning.el}</span> / <span lang="en">{r.meaning.en}</span></span>
              ))}
            </p>
            <Section b={P.visual} sub>
              <CheckTable rows={VEF_VISUAL} values={control.visual} onChange={setCheck('visual')} ro={ro} />
              <p className="vef-engines">
                <b><span lang="el">{VEF_ENGINES_NOTE.label.el}</span> / <span lang="en">{VEF_ENGINES_NOTE.label.en}</span>:</b>{' '}
                <span lang="el">{VEF_ENGINES_NOTE.text.el}</span>{' '}
                <span lang="en">{VEF_ENGINES_NOTE.text.en}</span>
              </p>
            </Section>
            <Section b={P.functional} sub>
              <CheckTable rows={VEF_FUNCTIONAL} values={control.functional} onChange={setCheck('functional')} ro={ro} />
            </Section>
            <Section b={P.record} sub>
              <div className="vef-grid">
                {field(L.inspectedBy, <input className="field" value={control.inspectedBy} maxLength={VESSEL_ENTRY_LIMITS.name} onChange={text('inspectedBy')} readOnly={ro} />)}
                {field(L.inspectedOn, dateInput(control.inspectedOn, text('inspectedOn')))}
                {field(L.technician, <input className="field" value={control.technician} maxLength={VESSEL_ENTRY_LIMITS.name} onChange={text('technician')} readOnly={ro} />)}
                <div className="vef-f" role="group" aria-labelledby={photosId}>
                  <span className="vef-l" id={photosId}><BiLabel b={L.photos} /></span>
                  <Choice<PhotosAnswer> value={control.photos} options={PHOTO_OPTIONS} onChange={(v) => setCtl('photos', v)} label="Photos: Yes or No" disabled={ro} />
                </div>
              </div>
              <p className="vef-hint vef-hint--end">The signature (<span lang="el">{L.signature.el}</span> / {L.signature.en}) is signed by hand on the printed form.</p>
            </Section>
          </Section>

          <Section b={S.admin}>
            <div className="vef-grid">
              {box(L.rolodexEntry, control.rolodexEntry, tick('rolodexEntry'))}
              {box(L.jobFileOpened, control.jobFileOpened, tick('jobFileOpened'))}
              {field(L.registeredOn, dateInput(control.registeredOn, text('registeredOn')))}
              {field(L.registeredBy, <input className="field" value={control.registeredBy} maxLength={VESSEL_ENTRY_LIMITS.name} onChange={text('registeredBy')} readOnly={ro} />)}
            </div>
          </Section>

          <Section b={S.scope}>
            <div className="vef-grid">
              {box(L.workRequestReceived, control.workRequestReceived, tick('workRequestReceived'))}
              {box(L.scopeIssued, control.scopeIssued, tick('scopeIssued'))}
              {area(L.criticalItems, control.criticalItems, text('criticalItems'), VESSEL_ENTRY_LIMITS.longText, 3)}
            </div>
          </Section>

          <Section b={S.schedule}>
            <div className="vef-grid vef-grid--3">
              {field(L.worksCommenced, dateInput(control.worksCommenced, text('worksCommenced')))}
              {field(L.estimatedCompletion, dateInput(control.estimatedCompletion, text('estimatedCompletion')))}
              {field(L.handover, dateInput(form.exitDate, set('exitDate')))}
            </div>
            {backwards ? (
              <p className="vef-hint vef-hint--end vef-hint--warn">
                The Handover Date ({df.date(form.exitDate)}) is before the Actual Arrival ({df.date(form.entryDate)}). If the boat is here for a new visit, clear the Handover Date; otherwise correct the Actual Arrival.
              </p>
            ) : closing && <p className="vef-hint vef-hint--end">Saved with a Handover Date, this visit's form is closed: the boat's next visit gets a new one.</p>}
          </Section>
        </div>
      </Modal>
      {confirmNode}
    </>,
    document.body,
  );
}
